/**
 * Owner: Masa Bayu
 * File: JustifiedTextBlock.tsx
 * Date:Time: 2026-10-05 10:34
 * Description: Fasa 5 · Face DOM from RenderInstruction[] · no typography math.
 * Rev: 2026-10-12 · Owner Open · D3 fix · flex item tidak boleh shrink/terbelah perkataan.
 */

import type { CSSProperties, ReactElement } from 'react';
import type { RenderInstruction } from '@qx/spsu-line-break-optimizer';

export type JustifiedTextBlockProps = {
  readonly instructions: readonly RenderInstruction[];
  readonly containerWidth?: number;
  /** Face sheet guna pt · ujian Fasa 5 kekal px. */
  readonly spacingUnit?: 'px' | 'pt';
};

/** Kumpul instructions kepada baris eksplisit (isLastInLine = penamat baris). */
export function groupInstructionsIntoLines(
  instructions: readonly RenderInstruction[],
): RenderInstruction[][] {
  const lines: RenderInstruction[][] = [];
  let current: RenderInstruction[] = [];
  for (const inst of instructions) {
    current.push(inst);
    if (inst.isLastInLine) {
      lines.push(current);
      current = [];
    }
  }
  if (current.length > 0) lines.push(current);
  return lines;
}

/**
 * D3 fix · span ialah flex item: flex-shrink asal 1 boleh belah perkataan
 * (WORD-SPLIT) · kekalkan saiz asli · white-space nowrap elak wrap dalam span.
 * margin-right hanya untuk glue · terakhir/tiada glue · tiada margin (elak overflow).
 */
const TOKEN_BASE_STYLE: CSSProperties = { flexShrink: 0, whiteSpace: 'nowrap' };

function tokenStyle(
  inst: RenderInstruction,
  spacingUnit: 'px' | 'pt',
): CSSProperties | undefined {
  if (inst.isLastInLine || inst.marginRightPx === 0) return TOKEN_BASE_STYLE;
  return { ...TOKEN_BASE_STYLE, marginRight: `${inst.marginRightPx}${spacingUnit}` };
}

/**
 * Tukang DOM sahaja · baca RenderInstruction, bina qx-justified-line.
 * Tiada flex-wrap / text-align:justify · putus baris sudah dari Core Engine.
 */
export function JustifiedTextBlock({
  instructions,
  containerWidth,
  spacingUnit = 'px',
}: JustifiedTextBlockProps): ReactElement {
  const lines = groupInstructionsIntoLines(instructions);
  const blockStyle: CSSProperties = {
    width:
      containerWidth != null ? `${containerWidth}${spacingUnit}` : '100%',
  };

  return (
    <div className="qx-justified-block" style={blockStyle} data-qx-justified-block="">
      {lines.map((line, lineIndex) => (
        <div
          key={`line-${lineIndex}`}
          className="qx-justified-line"
          data-qx-justified-line=""
          style={{ display: 'flex', width: '100%' }}
        >
          {line.map((token, tokenIndex) => (
            <span
              key={`t-${lineIndex}-${tokenIndex}`}
              style={tokenStyle(token, spacingUnit)}
            >
              {token.text}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
